import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PublicProvider, readListedProviders } from "services/provider-profile.service";
import { PLOT_TYPE_LABEL, PublicGravePlot, plotPlace, readAvailableGravePlots } from "services/grave-plots.service";
import {
  BundleDraft,
  PublicFacility,
  emptyDraft,
  readPublicFacilities,
  readPublicFreeTimes,
  sendRequest,
  validateDraft,
} from "services/marketplace.service";
import type { FreeTime } from "services/facilities.service";

const inputClass = "rounded-md border border-slate-300 px-3 py-2 text-sm";

const todayLocal = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const Step = ({ n, title, children }: { n: number; title: string; children: React.ReactNode }) => (
  <section className="rounded-[10px] border border-slate-200 bg-white p-6">
    <h2 className="mb-4 text-lg font-semibold text-slate-900">
      <span className="mr-2 text-slate-400">{n}.</span>
      {title}
    </h2>
    {children}
  </section>
);

const Choice = ({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`w-full rounded-md border px-4 py-3 text-left text-sm ${
      selected ? "border-slate-700 bg-slate-50" : "border-slate-200 hover:bg-slate-50"
    }`}
  >
    {children}
  </button>
);

/**
 * The Funely marketplace. A family chooses a funeral home and a resting place, and sends
 * one request; each provider replies to its own part. No sign-in, and nothing private.
 */
const MarketplacePage = () => {
  const navigate = useNavigate();
  const [draft, setDraft] = React.useState<BundleDraft>(emptyDraft());
  const [homes, setHomes] = React.useState<PublicProvider[]>([]);
  const [crematoriums, setCrematoriums] = React.useState<PublicProvider[]>([]);
  const [plots, setPlots] = React.useState<PublicGravePlot[]>([]);
  // The homepage links here with ?for=burial or ?for=cremation from its plot and crematorium sections.
  const [searchParams] = useSearchParams();
  const [restingPlace, setRestingPlace] = React.useState<"later" | "burial" | "cremation">(() => {
    const wanted = searchParams.get("for");
    return wanted === "burial" || wanted === "cremation" ? wanted : "later";
  });
  const [crematoriumId, setCrematoriumId] = React.useState(0);
  const [facilities, setFacilities] = React.useState<PublicFacility[]>([]);
  const [facilityId, setFacilityId] = React.useState(0);
  const [day, setDay] = React.useState(todayLocal());
  const [times, setTimes] = React.useState<FreeTime[]>([]);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const update = (changes: Partial<BundleDraft>) => setDraft((d) => ({ ...d, ...changes }));

  React.useEffect(() => {
    (async () => {
      const [h, c, p] = await Promise.all([
        readListedProviders("funeralHome"),
        readListedProviders("crematorium"),
        readAvailableGravePlots({}),
      ]);
      setHomes(h.data ?? []);
      setCrematoriums(c.data ?? []);
      setPlots(p.data ?? []);
    })();
  }, []);

  React.useEffect(() => {
    setFacilities([]);
    setFacilityId(0);
    if (!crematoriumId) return;
    (async () => {
      const { data } = await readPublicFacilities(crematoriumId);
      setFacilities(data ?? []);
    })();
  }, [crematoriumId]);

  React.useEffect(() => {
    setTimes([]);
    if (!crematoriumId || !facilityId || !day) return;
    (async () => {
      const from = new Date(`${day}T00:00:00`);
      const { data } = await readPublicFreeTimes(crematoriumId, facilityId, from, new Date(from.getTime() + 86400000));
      setTimes(data ?? []);
    })();
  }, [crematoriumId, facilityId, day]);

  const chooseRestingPlace = (choice: "later" | "burial" | "cremation") => {
    setRestingPlace(choice);
    update({ plot: null, cremation: null });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const invalid = validateDraft(draft);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setSending(true);
    const { data, error: sendError } = await sendRequest(draft);
    setSending(false);
    if (sendError || !data) {
      setError(sendError?.message ?? "The request could not be sent.");
      return;
    }
    navigate(`/marketplace/requests/${data.reference}`);
  };

  return (
    <main className="min-h-screen bg-stone-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-6 py-4">
          <span className="text-lg font-semibold text-slate-800">Funely</span>
        </div>
      </header>
      <div className="mx-auto flex max-w-3xl flex-col gap-5 px-6 py-10">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Arrange a funeral</h1>
          <p className="mt-2 text-slate-600">
            Choose a funeral home and a resting place, and send one request. Each of them replies to you
            directly; nothing is booked or paid until they do.
          </p>
        </div>

        <Step n={1} title="A funeral home">
          {homes.length === 0 ? (
            <p className="text-sm text-slate-500">No funeral homes are listed yet.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {homes.map((h) => (
                <Choice
                  key={h.orgId}
                  selected={draft.funeralHomeOrgId === h.orgId}
                  onClick={() => update({ funeralHomeOrgId: draft.funeralHomeOrgId === h.orgId ? null : h.orgId })}
                >
                  <span className="block font-medium text-slate-800">{h.name}</span>
                  {h.publicDescription && <span className="block text-xs text-slate-500 line-clamp-2">{h.publicDescription}</span>}
                </Choice>
              ))}
            </div>
          )}
        </Step>

        <Step n={2} title="A resting place">
          <div className="mb-4 flex gap-2">
            {(["burial", "cremation", "later"] as const).map((choice) => (
              <button
                key={choice}
                type="button"
                onClick={() => chooseRestingPlace(choice)}
                aria-pressed={restingPlace === choice}
                className={`rounded-full border px-3 py-1 text-sm ${
                  restingPlace === choice ? "border-slate-700 bg-slate-700 text-white" : "border-slate-300 text-slate-700"
                }`}
              >
                {choice === "burial" ? "A grave plot" : choice === "cremation" ? "A cremation" : "Decide later"}
              </button>
            ))}
          </div>

          {restingPlace === "burial" &&
            (plots.length === 0 ? (
              <p className="text-sm text-slate-500">No grave plots are available right now.</p>
            ) : (
              <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
                {plots.map((p) => (
                  <Choice
                    key={p.plotId}
                    selected={draft.plot?.plotId === p.plotId}
                    onClick={() => update({ plot: { orgId: p.orgId, plotId: p.plotId } })}
                  >
                    <span className="block font-medium text-slate-800">{p.cemeteryName}</span>
                    <span className="block text-xs text-slate-500">
                      {PLOT_TYPE_LABEL[p.plotType]} · {plotPlace(p)}
                    </span>
                  </Choice>
                ))}
              </div>
            ))}

          {restingPlace === "cremation" && (
            <div className="flex flex-col gap-3">
              <select className={inputClass} aria-label="Crematorium" value={crematoriumId} onChange={(e) => setCrematoriumId(Number(e.target.value))}>
                <option value={0}>Choose a crematorium…</option>
                {crematoriums.map((c) => (
                  <option key={c.orgId} value={c.orgId}>
                    {c.name}
                  </option>
                ))}
              </select>
              {crematoriumId > 0 && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <select className={inputClass} aria-label="Chapel or cremator" value={facilityId} onChange={(e) => setFacilityId(Number(e.target.value))}>
                    <option value={0}>Choose a chapel or cremator…</option>
                    {facilities.map((f) => (
                      <option key={f.resourceId} value={f.resourceId}>
                        {f.resourceName}
                      </option>
                    ))}
                  </select>
                  <input className={inputClass} aria-label="Day" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
                </div>
              )}
              {facilityId > 0 && (
                <div className="flex flex-wrap gap-2">
                  {times.length === 0 && <p className="text-sm text-slate-500">No free times on this day.</p>}
                  {times.map((t) => (
                    <button
                      key={t.start}
                      type="button"
                      aria-pressed={draft.cremation?.startsAt === t.start}
                      onClick={() => update({ cremation: { orgId: crematoriumId, resourceId: facilityId, startsAt: t.start } })}
                      className={`rounded-md border px-3 py-1.5 text-sm ${
                        draft.cremation?.startsAt === t.start ? "border-slate-700 bg-slate-700 text-white" : "border-slate-300"
                      }`}
                    >
                      {new Date(t.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {restingPlace === "later" && (
            <p className="text-sm text-slate-500">Your funeral home can help you choose once you have spoken.</p>
          )}
        </Step>

        <Step n={3} title="Who should they reply to?">
          <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Your name
              <input className={inputClass} value={draft.contactName} onChange={(e) => update({ contactName: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Name of the person who has died (optional)
              <input className={inputClass} value={draft.deceasedName} onChange={(e) => update({ deceasedName: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Email
              <input className={inputClass} type="email" value={draft.contactEmail} onChange={(e) => update({ contactEmail: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Phone
              <input className={inputClass} type="tel" value={draft.contactPhone} onChange={(e) => update({ contactPhone: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600 sm:col-span-2">
              Anything they should know (optional)
              <textarea className={inputClass} rows={3} value={draft.message} onChange={(e) => update({ message: e.target.value })} />
            </label>
            {error && <p className="text-sm text-red-600 sm:col-span-2">{error}</p>}
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={sending}
                className="rounded-md bg-slate-800 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send request"}
              </button>
            </div>
          </form>
        </Step>
      </div>
    </main>
  );
};

export default MarketplacePage;
